// src/features/preorders/components/PreOrdersTable.tsx

import React, { useState, useMemo } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
} from "@mui/material";
import i18n from "i18next";
import { useAppDispatch } from "../../../hooks";
import ActionsMenu from "./ActionsMenu";
import { updatePreorderStatus, fetchPreorders } from "../slices/preordersSlice";
import ConfirmationModal from "../../../components/custom-modal/ConfirmationModal";
import PreOrderDetailsModal from "./PreOrderDetailsModal";
import { formatPrice } from "../../../utils/formatPrice";
import MotifCell from "../../../components/custom-tooltip/MotifCell";

interface PreOrdersTableProps {
  rows: Preorder[];
}

interface PreOrdersTableHeader {
  id: number;
  header: string;
  textAlign?: string;
}

interface Preorder {
  NombreArticles: number;
  NumeroCommande: number;
  StatutSolde: string | null;
  StatutLivraison: string | null;
  StatutFacturation: string | null;
  STATUSCOMMANDE: string;
  StatutFinal: string | null;
  NumeroClient: string;
  NumeroRepresentant: string | null;
  DateCommande: string;
  MontantTTC: number;
  IsExistsPyramide: boolean;
  NomClient: string;
  Motif: string | null;
  UpdatedOn: string;
}

const PreOrdersTable: React.FC<PreOrdersTableProps> = ({ rows }) => {
  const dispatch = useAppDispatch();

  const [modalOpen, setModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedPreorder, setSelectedPreorder] = useState<Preorder | null>(
    null
  );
  const [action, setAction] = useState("");

  const columns = useMemo<PreOrdersTableHeader[]>(
    () => [
      { id: 1, header: i18n.t("preOrders.columnHeaders.N") },
      { id: 2, header: i18n.t("preOrders.columnHeaders.client") },
      { id: 3, header: i18n.t("preOrders.columnHeaders.nameClient") },
      { id: 4, header: i18n.t("preOrders.columnHeaders.orderDate") },
      { id: 5, header: i18n.t("preOrders.columnHeaders.saleSite") },
      { id: 6, header: i18n.t("preOrders.columnHeaders.expeditionSite") },
      { id: 7, header: i18n.t("preOrders.columnHeaders.orderDateUpdate") },
      { id: 8, header: i18n.t("preOrders.columnHeaders.motif") },

      { id: 9, header: i18n.t("preOrders.columnHeaders.status") },
      {
        id: 10,
        header: i18n.t("preOrders.columnHeaders.actions"),
        textAlign: "center",
      },
    ],
    []
  );

  const handleOpenModal = (preorder: Preorder, action: string) => {
    setSelectedPreorder(preorder);
    setAction(action);
    setModalOpen(true);
  };

  const handleOpenDetailsModal = (preorder: Preorder) => {
    setSelectedPreorder(preorder);
    setDetailsModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedPreorder(null);
    setAction("");
  };

  const handleCloseDetailsModal = () => {
    setDetailsModalOpen(false);
    setSelectedPreorder(null);
  };

  const handleConfirm = async () => {
    if (selectedPreorder) {
      const statutId = action === "validate" ? 3 : 2;
      await dispatch(
        updatePreorderStatus({
          statutId,
          precommandeId: selectedPreorder.NumeroCommande,
        })
      );
      handleCloseModal();
      dispatch(fetchPreorders()); // Re-fetch the preorders list
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Created":
        return "success";
      case "Validated ":
        return "primary";
      case "Pending":
        return "warning";
      case "Canceled":
        return "error";
      default:
        return "default";
    }
  };


  return (
    <>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ minHeight: "50vh" }}
      >
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((column, index) => (
                <TableCell
                  key={index}
                  sx={{
                    color: "#C1C1D0",
                    fontFamily: "Poppins",
                    textTransform: "uppercase",
                    textAlign: column.textAlign ?? "left",
                  }}
                >
                  {column.header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={index}>
                <TableCell>{row.NumeroCommande}</TableCell>
                <TableCell>{row.NumeroClient}</TableCell>
                <TableCell>{row.NomClient}</TableCell>
                <TableCell>
                  {new Date(row.DateCommande).toLocaleString()}
                </TableCell>
                <TableCell>{formatPrice(row.MontantTTC)}</TableCell>
                <TableCell>{row.NombreArticles}</TableCell>
                <TableCell>
                  {row.STATUSCOMMANDE !== "Pending" ? new Date(row.UpdatedOn).toLocaleString() : "-"}
                </TableCell>
                <TableCell>
                  <MotifCell motif={row.Motif} />
                </TableCell>

                <TableCell>
                  <Chip
                    label={row.STATUSCOMMANDE}
                    color={getStatusColor(row.STATUSCOMMANDE)}
                  />
                </TableCell>
                <TableCell sx={{ width: "110px" }}>
                  <ActionsMenu
                    onValidate={() => handleOpenModal(row, "validate")}
                    onRefuse={() => handleOpenModal(row, "refuse")}
                    onConsult={() => handleOpenDetailsModal(row)}
                    status={row.STATUSCOMMANDE} // Pass status as a prop
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {selectedPreorder && (
        <ConfirmationModal
          open={modalOpen}
          handleClose={handleCloseModal}
          handleConfirm={handleConfirm}
          title={
            action === "validate"
              ? "Confirmer la validation"
              : "Confirmer le refus"
          }
          content={`Êtes-vous sûr de vouloir ${action} 
la précommande ${selectedPreorder.NumeroCommande}?`}
        />
      )}
      {selectedPreorder && (
        <PreOrderDetailsModal
          open={detailsModalOpen}
          handleClose={handleCloseDetailsModal}
          preOrderId={selectedPreorder.NumeroCommande.toString()}
        />
      )}
    </>
  );
};

export default PreOrdersTable;
